/* eslint-disable @typescript-eslint/no-floating-promises, parog-web/no-glint-expect-error-directive */
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import type { EmberChangeset } from 'ember-changeset';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import ArchiveJournalEventButton from 'parog-web/components/archive-journal-event-button';
import type CrudJournalEventBasedComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Base from 'parog-web/components/forc/provider/base';
import Footer from 'parog-web/components/forc/provider/footer';
import type Patient from 'parog-web/models/patient';
import PageContainer from 'ui-kit/components/page-container';
import type ForcJournalEventBasedComponent from './abstract/journal-event-based-component';
import type { Signature as ForcProviderBaseSignature } from './provider/base';

// Subclasses of these two abstract components each require their own
// specific Args, so this is kept wide rather than tied to one shape.
/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyForcComponent =
  | ForcJournalEventBasedComponent<any>
  | CrudJournalEventBasedComponent<any, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface Signature {
  Args: {
    patient?: Patient;
    changeset?: EmberChangeset;
    isTextWidthLimitDisabled?: boolean;
    isHeaderHidden?: boolean;
    background?: 'transparent';
    forcComponent: AnyForcComponent;
    title?: string;
    class?: string;
    hasSmallFooter?: boolean;
  };
  Blocks: {
    header: [];
    content: [baseProvider: ForcProviderBaseSignature['Blocks']['default'][0]];
  };
}

export default class ForcProviderComponent extends Component<Signature> {
  @service declare abilities: AbilitiesService;

  get displayTitle() {
    return this.args.title
      ? this.args.title
      : this.#forcComponent.forcDisplayName;
  }

  get shouldDisplayArchiveButton() {
    return (
      this.abilities.can('archive form', this.#forcComponent.fullComponentId) &&
      this.#forcComponent.isEditMode &&
      !this.#forcComponent.isReadOnly
    );
  }

  get #forcComponent() {
    return this.args.forcComponent;
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    if (!this.args.forcComponent) {
      throw `Mandatory parameter missing in ForcProviderComponent: forcComponent`;
    }
  }

  // not awaited because it makes test 27_2 fail
  onSubmit = event => {
    event.preventDefault();
    this.#forcComponent.onSubmit.perform();
  };

  <template>
    {{!
  @title - The title of the current page
  @forcComponent - An object inherited from 'forc/abstract/base-component' which contains the configuration for the provider :
    @changeset {Changeset} - A changeset if needed but as a TODO: we should provide components which works with and without changeset
    @isReadOnly {Boolean}
    @onCancel {Function}
    @onSubmit {Function}
    @cancelButtonTitle {String}
    @submitButtonTitle {String}
    @background {String}
}}
    <PageContainer
      @title={{this.displayTitle}}
      @background={{@background}}
      @headerHidden={{or @isHeaderHidden (not this.displayTitle)}}
      @retrievedJournalEvent={{@forcComponent.retrievedJournalEvent}}
      @dontUseTextWidthLimit={{@isTextWidthLimitDisabled}}
      @class={{@class}}
    >
      <:headerWidget>
        {{yield to='header'}}
        {{#if this.shouldDisplayArchiveButton}}
          <ArchiveJournalEventButton
            {{! @glint-expect-error }}
            @journalEvent={{@forcComponent.sourceJournalEvent}}
            @dataTest={{@forcComponent.journalEventClass}}
            @afterArchived={{@forcComponent.afterCompleted}}
            @iconSize='small'
            @confirmationTitle='Êtes-vous sûr ?'
            class='ml-[1em]'
          >Supprimer cette entrée</ArchiveJournalEventButton>
        {{/if}}
      </:headerWidget>
      <:content>
        <form class='ui form' {{on 'submit' this.onSubmit}}>
          <Base
            @changeset={{if @changeset @changeset @forcComponent.changeset}}
            @isReadOnly={{@forcComponent.isReadOnly}}
            as |BaseProvider|
          >
            {{yield BaseProvider to='content'}}

            <Footer
              @hasSmallFooter={{@hasSmallFooter}}
              @forcComponent={{@forcComponent}}
              as |Footer|
            >
              {{#if (has-block 'footer')}}

                {{! @glint-expect-error }}
                {{yield Footer to='footer'}}
              {{else}}
                <Footer.CancelButton /><Footer.SubmitButton />
              {{/if}}
            </Footer>
          </Base>
        </form>
      </:content>
    </PageContainer>
  </template>
}
